import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { ReportFilters, defaultReportFilters, type ReportFilterState } from './report-filters';
import { exportStockMizan } from '@/lib/export-stock-reports';

const fmt = (n: number) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

export function StockTrialBalanceReport() {
  const { t } = useTranslation('trading');
  const [filters, setFilters] = useState<ReportFilterState>(defaultReportFilters);

  const { data, isLoading } = trpc.stockReports.getStockTrialBalance.useQuery({
    dateFrom: new Date(filters.dateFrom),
    dateTo: new Date(filters.dateTo + 'T23:59:59'),
    warehouseId: filters.warehouseId || undefined,
    brandId: filters.brandId || undefined,
    search: filters.search || undefined,
  });

  const warehouses = data?.warehouses ?? [];
  const hasData = warehouses.some((w) => w.rows.length > 0);

  return (
    <div className="space-y-4">
      <ReportFilters
        value={filters}
        onChange={setFilters}
        show={{ warehouse: true, brand: true, search: true }}
        onExport={() => exportStockMizan(warehouses)}
        exporting={false}
        exportDisabled={!hasData}
      />

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="px-2 py-2.5 text-left font-medium">{t('reports.code', 'Kod')}</th>
              <th className="px-2 py-2.5 text-left font-medium">{t('reports.product', 'Ürün')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.openingQty', 'Açılış Mik.')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.inQty', 'Giren')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.outQty', 'Çıkan')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.closingQty', 'Kalan')}</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.openingValue', 'Açılış Değer')} (TL)</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.inValue', 'Giren Değer')} (TL)</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.outValue', 'Çıkan Değer')} (TL)</th>
              <th className="px-2 py-2.5 text-right font-medium">{t('reports.closingValue', 'Kalan Değer')} (TL)</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={10} className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></td></tr>
            )}
            {!isLoading && !hasData && (
              <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">{t('reports.noResults', 'Kayıt bulunamadı')}</td></tr>
            )}
            {warehouses.map((w) => (
              <Fragment key={w.warehouseId}>
                <tr className="border-b bg-muted/30">
                  <td colSpan={10} className="px-2 py-2 font-semibold">{w.warehouseName}</td>
                </tr>
                {w.rows.map((r, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="whitespace-nowrap px-2 py-2 font-mono text-xs">{r.productCode}</td>
                    <td className="max-w-[240px] px-2 py-2"><span className="block truncate">{r.productName}</span></td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.openingQty || ''}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.inQty || ''}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.outQty || ''}</td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium">{r.closingQty}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.openingValue ? fmt(r.openingValue) : ''}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.inValue ? fmt(r.inValue) : ''}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{r.outValue ? fmt(r.outValue) : ''}</td>
                    <td className={`px-2 py-2 text-right tabular-nums font-medium ${r.closingValue < 0 ? 'text-red-600' : ''}`}>{fmt(r.closingValue)}</td>
                  </tr>
                ))}
                <tr className="border-b-2 bg-muted/40 font-semibold">
                  <td className="px-2 py-2" colSpan={6}>{w.warehouseName} {t('reports.subtotal', 'Toplamı')}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmt(w.subtotal.openingValue)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmt(w.subtotal.inValue)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmt(w.subtotal.outValue)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{fmt(w.subtotal.closingValue)}</td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('reports.mizanNote', 'Açılış bakiyesi, başlangıç tarihinden önceki tüm stok hareketlerinden hesaplanır. Negatif kalan, eksik alış/devir hareketine işaret eder.')}
      </p>
    </div>
  );
}
